import { FC, RefObject } from 'react';
import { defineMessages, MessageDescriptor } from 'react-intl';
import {
  FormikProps,
  FormikContextType,
} from 'formik';

import { Actions, RequestTypes } from './index';
import { StoreShape, ThunkAction } from 'hellospa/redux/types';
import * as actions from 'hellospa/page/prep-and-send/data/actions';
import DocumentQuotaAlert from 'hellospa/components/prep-and-send/quota-alerts/document-quota-alert';
import TemplateQuotaAlert from 'hellospa/components/prep-and-send/quota-alerts/template-quota-alert';
import FileUploaderPage from 'hellospa/components/prep-and-send/file-uploader';
import RecipientPage from 'hellospa/components/prep-and-send/recipient';
import PlaceFieldsPage, { containerClassName } from 'hellospa/components/prep-and-send/place-fields';
import MergeFieldsPage from 'hellospa/components/deep-integration/merge-fields';
import ReviewPage, { reviewTriggerComponents } from 'hellospa/components/prep-and-send/review';
import DropinPage from 'hellospa/components/deep-integration/dropin';

import DocumentIcon from 'hellospa/components/prep-and-send/stepper/icons/document';
import SignersIcon from 'hellospa/components/prep-and-send/stepper/icons/signers';
import FieldsIcon from 'hellospa/components/prep-and-send/stepper/icons/fields';
import ReviewIcon from 'hellospa/components/prep-and-send/stepper/icons/review';
import DeepIntegrationFinalLoading from 'hellospa/components/deep-integration/final-loading';
import DeepIntegrationFinalLoadingForTemplate from 'hellospa/components/deep-integration/final-loading/final-loading-for-template';
import EmbeddedPreLoading from 'hellospa/components/prep-and-send/embedded/pre-loading';
import FileIcon from 'hellospa/components/prep-and-send/stepper/icons/file';

// All Page's getText() functions must return an object of defineMessages()
export type PageText = {
  title?: MessageDescriptor,
  next?: MessageDescriptor,
  shortNext?: MessageDescriptor,
}

const emptyText: PageText = defineMessages({
  title: {
    id: '',
    description: 'emptyTextTitleDescription',
    defaultMessage: 'emptyTextTitleDefault',
  },
  next: {
    id: '',
    description: 'emptyTextNextDescription',
    defaultMessage: 'emptyTextNextDefault',
  },
});

// An array of redux-thunk action callbacks
type ThunkActions = () => ThunkAction<void>;

export type FormikRefType = RefObject<FormikProps<{}>>;
export type OnPreviousCallback = () => Promise<unknown>;

export type TriggerStage =
  | 'load'
  | 'submit'
  | 'done'

type TriggerComponentProps = {
  onCancel: () => void,
  onSkip: () => void,
  stage: TriggerStage,
}

export type TriggerComponent<P = {}> = FC<P & TriggerComponentProps> & {
  needsTrigger: (
    state: StoreShape,
    formik: FormikContextType<any>,
  ) => boolean,
}

export type TriggerStageComponents = {
  [key in TriggerStage]: TriggerComponent[]
}

export type TriggerCallback<TSubmit> = {
  load: () => void,
  submit: TSubmit,
  done: () => void,
}

type WorkflowComponentProps = {
  /**
   * Token used to make POST API requests
   */
  csrfToken?: string,

  // Called on component's Formik.onSubmit()
  onSaveActions: ThunkActions[],

  /**
   * Used by the Trigger hook to automatically call stage-based components
   */
  triggers: TriggerStageComponents,

  /**
   * Used by Stepper next/back buttons to trigger active Page Formik.submitForm()
   * However, <Formik innerRef> is incorrectly typed so we can't pass the ref
   * object directly. Use useFormikRef() below to quiet TS
   */
  formRef: FormikRefType,

  /**
   * Called when the "Back" button is clicked from the place fields step
   * to trigger an autoSave of data from the Editor.
   */
  onPreviousCallbackRef?: React.MutableRefObject<OnPreviousCallback | null>,
}

/**
 * This is actually RefObject<FormikProps{} | undefined> but
 * Formik's type definition is not correct
 * see https://github.com/jaredpalmer/formik/issues/2290
 */
export const useFormikRef = (ref: FormikRefType) => {
  // @ts-ignore
  return ref as (instance: any) => void;
};

/**
 * All Pages will be passed WorkflowComponentProps via props,
 * and must implement validates() and getText() functions
 */
export type WorkflowComponent<P = {}> = FC<P & WorkflowComponentProps> & {
  getText: (workflow: Workflow, state: StoreShape) => PageText,
}

/**
 * All Page definitions implement the following. Some properties will be
 * set at run-time by actions/workflow whenever a Workflow is changed or set
 */
interface Page {
  component: WorkflowComponent,
  // icon is memoized SVG element
  icon: FC,
  path: string,
  // Set by actions/workflow.configure()
  text: PageText,
  onSave: ThunkActions[],
  triggers: TriggerStageComponents,
  // Local override to hide the stepper.
  hideProgressStepper?: boolean,
  // decide wether or not this page shows footer
  hideFooter: boolean,
  // hides controls in stepper
  hideStepperBtnContainer?: boolean,
  hidePoweredBy?: boolean,
  hideMergeFields?: boolean,
  stepperMobileWidth?: number
  containerClassName?: string,
  disableBack?: boolean,
}

/**
 * Default action to take when user clicks "Next" button on Stepper
 * Can be changed at Page level for each Workflow
 */
const defaultOnSave = [
  // Save needs to happen before moving to the next page. When this was reversed
  // the Place Fields page was able to start trying to prepare the Editor before
  // the save from leaving the recipients page completed
  actions.save,
  actions.nextPageInWorkflow,
];

export const defaultTrigger: TriggerStageComponents = {
  load: [],
  submit: [],
  done: [],
};

const finalOnSave = [
  actions.sendSRAndRedirect,
];

const pageDefaults = {
  validates: false,
  text: emptyText,
  onSave: defaultOnSave,
  triggers: defaultTrigger,
  hideFooter: false,
};

export const pages = {
  FileUploaderPage: (props: Partial<Page>): Page => ({
    ...pageDefaults,
    component: FileUploaderPage,
    icon: DocumentIcon,
    path: 'documents',
    triggers: {
      ...defaultTrigger,
      load: [
        TemplateQuotaAlert,
        DocumentQuotaAlert,
      ],
    },
    ...props,
  }),
  RecipientPage: (props: Partial<Page>): Page => ({
    ...pageDefaults,
    component: RecipientPage,
    icon: SignersIcon,
    path: 'recipients',
    ...props,
  }),
  PlaceFieldsPage: (props: Partial<Page>): Page => ({
    ...pageDefaults,
    component: PlaceFieldsPage,
    icon: FieldsIcon,
    path: 'place-fields',
    hideFooter: true,
    hidePoweredBy: true,
    containerClassName,
    ...props,
  }),
  MergeFieldsPage: (props: Partial<Page>): Page => ({
    ...pageDefaults,
    component: MergeFieldsPage,
    icon: FieldsIcon,
    path: 'merge-fields',
    hideFooter: true,
    hidePoweredBy: true,
    containerClassName,
    ...props,
  }),
  ReviewPage: (props: Partial<Page>): Page => ({
    ...pageDefaults,
    component: ReviewPage,
    icon: ReviewIcon,
    path: 'review',
    onSave: finalOnSave,
    triggers: {
      ...defaultTrigger,
      submit: [
        DocumentQuotaAlert,
        reviewTriggerComponents.UpgradeSendEmailModal,
        reviewTriggerComponents.UpgradeSendMultiFaxModal,
        reviewTriggerComponents.SignerInfoModal,
      ],
    },
    ...props,
  }),
  EmbeddedPreLoadingPage: (props: Partial<Page>): Page => ({
    ...pageDefaults,
    component: EmbeddedPreLoading,
    icon: FileIcon,
    path: 'embedded-pre-loading',
    hideStepperBtnContainer: true,
    ...props,
  }),
  DeepIntegrationDropinPage: (props: Partial<Page>): Page => ({
    ...pageDefaults,
    component: DropinPage,
    icon: FileIcon,
    path: 'dropin',
    ...props,
  }),
  DeepIntegrationFinalLoading: (props: Partial<Page>): Page => ({
    ...pageDefaults,
    component: DeepIntegrationFinalLoading,
    icon: SignersIcon,
    path: 'deep-integration-save-back-loading',
    hideStepperBtnContainer: true,
    ...props,
  }),
  DeepIntegrationFinalLoadingForTemplate: (props: Partial<Page>): Page => ({
    ...pageDefaults,
    component: DeepIntegrationFinalLoadingForTemplate,
    icon: SignersIcon,
    path: 'deep-integration-saving-template-loading',
    hideStepperBtnContainer: true,
    ...props,
  }),
  DeepIntegrationSelfSignFinalLoading: (props: Partial<Page>): Page => ({
    ...pageDefaults,
    component: DeepIntegrationFinalLoading,
    icon: SignersIcon,
    path: 'deep-integration-save-back-loading',
    hideStepperBtnContainer: true,
    ...props,
  }),
};

export enum WorkflowTags {
  DeepIntegration,
  DeepIntegrationSelfSign,
  SignatureRequestEmail,
  SignatureRequest,
  SignatureRequestUnknown,
  SignatureRequestDocument,
  SignatureRequestTemplate,
  SignatureRequestFax,
  CreateTemplate,
  CreateTemplateLink,
  HideStepperToolbar,
  BulkSend,
  ShowInlinePreview,
  IntegrationOneTimeDoc,
}

export interface Workflow {
  // useful for tying frontend workflow type to backend request type when
  // saving to the backend
  requestType: RequestTypes,
  tags: WorkflowTags[],
  name: string,
  pages: Page[],
}
export const FAX_NO_EDITOR = 'fax-no-editor';
export const workflows = {
  SignatureRequestUnknown: {
    requestType: RequestTypes.RequestSig,
    tags: [
      WorkflowTags.SignatureRequest,
      WorkflowTags.SignatureRequestUnknown,
    ],
    name: 'unknown',
    pages: [
      pages.FileUploaderPage({ onSave: [
        actions.changeWorkflowFromFiles,
        ...defaultOnSave,
      ] }),
      pages.RecipientPage({}),
      pages.PlaceFieldsPage({}),
      pages.ReviewPage({}),
    ],
  },
  SignatureRequestDocument: {
    requestType: RequestTypes.RequestSig,
    tags: [
      WorkflowTags.SignatureRequest,
      WorkflowTags.SignatureRequestDocument,
    ],
    name: 'one-time doc',
    pages: [
      pages.FileUploaderPage({ onSave: [
        actions.changeWorkflowFromFiles,
        ...defaultOnSave,
      ] }),
      pages.RecipientPage({}),
      pages.PlaceFieldsPage({}),
      pages.ReviewPage({}),
    ],
  },
  SignatureRequestTemplate: {
    requestType: RequestTypes.SendTemplate,
    tags: [
      WorkflowTags.SignatureRequest,
      WorkflowTags.SignatureRequestTemplate,
    ],
    name: 'template',
    pages: [
      pages.FileUploaderPage({ onSave: [
        actions.changeWorkflowFromFiles,
        ...defaultOnSave,
      ] }),
      pages.RecipientPage({}),
      pages.ReviewPage({}),
    ],
  },
  SignatureRequestTemplateAndDocument: {
    requestType: RequestTypes.SendTemplate,
    tags: [
      WorkflowTags.SignatureRequest,
      WorkflowTags.SignatureRequestDocument,
      WorkflowTags.SignatureRequestTemplate,
    ],
    name: 'template with one-time doc',
    pages: [
      pages.FileUploaderPage({ onSave: [
        actions.changeWorkflowFromFiles,
        ...defaultOnSave,
      ] }),
      pages.RecipientPage({}),
      pages.PlaceFieldsPage({}),
      pages.ReviewPage({}),
    ],
  },
  SignatureRequestFax: {
    requestType: RequestTypes.SendDoc,
    tags: [
      WorkflowTags.SignatureRequest,
      WorkflowTags.SignatureRequestFax,
    ],
    name: 'fax',
    pages: [
      pages.FileUploaderPage({}),
      pages.PlaceFieldsPage({}),
      pages.ReviewPage({}),
    ],
  },
  SignatureRequestFaxNoEditor: {
    requestType: RequestTypes.SendDoc,
    tags: [
      WorkflowTags.SignatureRequest,
      WorkflowTags.SignatureRequestFax,
    ],
    name: FAX_NO_EDITOR,
    pages: [
      pages.FileUploaderPage({}),
      pages.ReviewPage({}),
    ],
  },
  CreateTemplate: {
    requestType: RequestTypes.Template,
    tags: [
      WorkflowTags.CreateTemplate,
    ],
    name: 'create template',
    pages: [
      pages.FileUploaderPage({}),
      pages.RecipientPage({}),
      pages.PlaceFieldsPage({}),
      pages.ReviewPage({}),
    ],
  },
  CreateTemplateLink: {
    requestType: RequestTypes.ReusableLink,
    tags: [
      WorkflowTags.CreateTemplateLink,
    ],
    name: 'create template link',
    pages: [
      pages.FileUploaderPage({}),
      pages.RecipientPage({}),
      pages.PlaceFieldsPage({}),
      pages.ReviewPage({}),
    ],
  },
  BulkSend: {
    requestType: RequestTypes.BulkSend,
    tags: [
      WorkflowTags.BulkSend,
    ],
    name: 'bulk send',
    pages: [
      pages.FileUploaderPage({
        onSave: [
          actions.initBulkSend,
          actions.getBulkSendExample,
          actions.nextPageInWorkflow,
          actions.save,
        ],
      }),
      pages.RecipientPage({}),
      pages.ReviewPage({
        onSave: [
          actions.sendBulkRequestAndRedirect,
        ],
      }),
    ],
  },
  DeepIntegration: {
    requestType: RequestTypes.EmbeddedRequest,
    tags: [
      WorkflowTags.DeepIntegration,
      WorkflowTags.SignatureRequestEmail,
      WorkflowTags.SignatureRequest,
      WorkflowTags.SignatureRequestDocument,
    ],
    name: 'deep integration',
    pages: [
      pages.RecipientPage({ onSave: [
        actions.changeWorkflowDeepIntegration,
        ...defaultOnSave,
      ] }),
      pages.PlaceFieldsPage({ onSave: [
        actions.changeWorkflowDeepIntegration,
        ...defaultOnSave,
      ] }),
      pages.DeepIntegrationDropinPage({}),
      pages.ReviewPage({}),
      pages.DeepIntegrationFinalLoading({}),
    ],
  },
  DeepIntegrationSelfSign: {
    requestType: RequestTypes.EmbeddedRequest,
    tags: [
      WorkflowTags.DeepIntegration,
      WorkflowTags.DeepIntegrationSelfSign,
      WorkflowTags.SignatureRequestEmail,
      WorkflowTags.SignatureRequest,
      WorkflowTags.SignatureRequestDocument,
    ],
    name: 'deep integration self-sign',
    pages: [
      pages.RecipientPage({ onSave: [
        actions.changeWorkflowDeepIntegration,
        ...defaultOnSave,
      ] }),
      pages.PlaceFieldsPage({ onSave: [
        actions.changeWorkflowDeepIntegration,
        ...defaultOnSave,
      ] }),
      pages.DeepIntegrationDropinPage({
        onSave: [
          actions.send,
        ],
      }),
      pages.DeepIntegrationSelfSignFinalLoading({}),
    ],
  },
  DeepIntegrationTemplate: {
    requestType: RequestTypes.EmbeddedTemplate,
    tags: [
      WorkflowTags.DeepIntegration,
      WorkflowTags.CreateTemplate,
    ],
    name: 'deep integration template',
    pages: [
      pages.EmbeddedPreLoadingPage({
        disableBack: true,
      }),
      pages.RecipientPage({ onSave: [
        actions.changeWorkflowDeepIntegration,
        ...defaultOnSave,
      ] }),
      pages.PlaceFieldsPage({ onSave: [
        actions.changeWorkflowDeepIntegration,
        ...defaultOnSave,
      ] }),
      pages.ReviewPage({}),
      pages.DeepIntegrationFinalLoadingForTemplate({}),
    ],
  },
  DeepIntegrationEditTemplate: {
    requestType: RequestTypes.EmbeddedTemplate,
    tags: [
      WorkflowTags.DeepIntegration,
      WorkflowTags.CreateTemplate,
    ],
    name: 'deep integration edit template',
    pages: [
      pages.EmbeddedPreLoadingPage({
        disableBack: true,
      }),
      pages.RecipientPage({ onSave: [
        actions.changeWorkflowDeepIntegration,
        ...defaultOnSave,
      ] }),
      pages.PlaceFieldsPage({ onSave: [
        actions.changeWorkflowDeepIntegration,
        ...defaultOnSave,
      ] }),
      pages.ReviewPage({}),
      pages.DeepIntegrationFinalLoadingForTemplate({}),
    ],
  },
  DeepIntegrationUseTemplate: {
    requestType: RequestTypes.EmbeddedRequest,
    tags: [
      WorkflowTags.DeepIntegration,
      WorkflowTags.SignatureRequestTemplate,
      WorkflowTags.ShowInlinePreview,
    ],
    name: 'deep integration use template',
    pages: [
      pages.EmbeddedPreLoadingPage({
        disableBack: true,
      }),
      pages.RecipientPage({
        onSave: [
          actions.changeWorkflowUseTemplate,
          ...defaultOnSave,
        ],
      }),
      pages.ReviewPage({
        hideMergeFields: true,
      }),
    ],
  },
  DeepIntegrationUseTemplatesMergeFields: {
    requestType: RequestTypes.EmbeddedRequest,
    tags: [
      WorkflowTags.DeepIntegration,
      WorkflowTags.SignatureRequestTemplate,
      WorkflowTags.ShowInlinePreview,
    ],
    name: 'deep integration use template no merge fields',
    pages: [
      pages.EmbeddedPreLoadingPage({
        disableBack: true,
      }),
      pages.RecipientPage({}),
      pages.MergeFieldsPage({}),
      pages.ReviewPage({
        hideMergeFields: true,
      }),
    ],
  },
  Mobile: {
    requestType: RequestTypes.EmbeddedRequest,
    tags: [
      WorkflowTags.SignatureRequestDocument,
    ],
    name: 'embedded request - show review',
    pages: [
      pages.EmbeddedPreLoadingPage({}),
      pages.PlaceFieldsPage({
        onSave: finalOnSave,
      }),
    ],
  },
  MobileSelfSign: {
    requestType: RequestTypes.SelfSave,
    tags: [
      WorkflowTags.SignatureRequestDocument,
    ],
    name: 'embedded request - show review',
    pages: [
      pages.EmbeddedPreLoadingPage({}),
      pages.PlaceFieldsPage({
        onSave: [
          actions.selfSaveAfterEditor,
          // ...finalOnSave,
        ],
      }),
    ],
  },

  EmbeddedTemplate: {
    requestType: RequestTypes.EmbeddedTemplate,
    tags: [
      WorkflowTags.CreateTemplate,
      WorkflowTags.HideStepperToolbar,
    ],
    name: 'embedded template',
    pages: [
      pages.EmbeddedPreLoadingPage({}),
      pages.PlaceFieldsPage({
        onSave: finalOnSave,
      }),
    ],
  },
  EmbeddedTemplateRecipient: {
    requestType: RequestTypes.EmbeddedTemplate,
    tags: [
      WorkflowTags.CreateTemplate,
    ],
    name: 'embedded template - show signers',
    pages: [
      pages.EmbeddedPreLoadingPage({}),
      pages.RecipientPage({}),
      pages.PlaceFieldsPage({
        onSave: finalOnSave,
      }),
    ],
  },
  EmbeddedTemplateReview: {
    requestType: RequestTypes.EmbeddedTemplate,
    tags: [
      WorkflowTags.CreateTemplate,
    ],
    name: 'embedded template - show review',
    pages: [
      pages.EmbeddedPreLoadingPage({}),
      pages.PlaceFieldsPage({}),
      pages.ReviewPage({}),
    ],
  },
  EmbeddedTemplateRecipientReview: {
    requestType: RequestTypes.EmbeddedTemplate,
    tags: [
      WorkflowTags.CreateTemplate,
    ],
    name: 'embedded template - show signers and review',
    pages: [
      pages.EmbeddedPreLoadingPage({}),
      pages.RecipientPage({}),
      pages.PlaceFieldsPage({}),
      pages.ReviewPage({}),
    ],
  },
  IntegrationCreateTemplate: {
    requestType: RequestTypes.EmbeddedTemplate,
    tags: [
      WorkflowTags.CreateTemplate,
    ],
    name: 'embedded template - show signers and review',
    pages: [
      pages.EmbeddedPreLoadingPage({}),
      pages.FileUploaderPage({}),
      pages.RecipientPage({}),
      pages.PlaceFieldsPage({}),
      pages.ReviewPage({}),
    ],
  },
  EmbeddedRequest: {
    requestType: RequestTypes.EmbeddedRequest,
    tags: [
      WorkflowTags.SignatureRequestDocument,
      WorkflowTags.HideStepperToolbar,
    ],
    name: 'embedded request',
    pages: [
      pages.EmbeddedPreLoadingPage({}),
      pages.PlaceFieldsPage({
        onSave: finalOnSave,
      }),
    ],
  },
  EmbeddedRequestRecipient: {
    requestType: RequestTypes.EmbeddedRequest,
    tags: [
      WorkflowTags.SignatureRequestDocument,
    ],
    name: 'embedded request - show signers',
    pages: [
      pages.EmbeddedPreLoadingPage({}),
      pages.RecipientPage({}),
      pages.PlaceFieldsPage({
        onSave: finalOnSave,
      }),
    ],
  },
  EmbeddedRequestReview: {
    requestType: RequestTypes.EmbeddedRequest,
    tags: [
      WorkflowTags.SignatureRequestDocument,
    ],
    name: 'embedded request - show review',
    pages: [
      pages.EmbeddedPreLoadingPage({}),
      pages.PlaceFieldsPage({}),
      pages.ReviewPage({}),
    ],
  },
  EmbeddedRequestRecipientReview: {
    requestType: RequestTypes.EmbeddedRequest,
    tags: [
      WorkflowTags.SignatureRequestDocument,
    ],
    name: 'embedded request - show signers and review',
    pages: [
      pages.EmbeddedPreLoadingPage({}),
      pages.RecipientPage({}),
      pages.PlaceFieldsPage({}),
      pages.ReviewPage({}),
    ],
  },
  EmbeddedRequestWithTemplate: {
    requestType: RequestTypes.EmbeddedRequest,
    tags: [
      WorkflowTags.SignatureRequestTemplate,
      WorkflowTags.HideStepperToolbar,
    ],
    name: 'embedded request with template',
    pages: [
      pages.EmbeddedPreLoadingPage({}),
      pages.PlaceFieldsPage({
        onSave: finalOnSave,
      }),
    ],
  },
  EmbeddedRequestRecipientWithTemplate: {
    requestType: RequestTypes.EmbeddedRequest,
    tags: [
      WorkflowTags.SignatureRequestTemplate,
    ],
    name: 'embedded request with template - show signers',
    pages: [
      pages.EmbeddedPreLoadingPage({}),
      pages.RecipientPage({}),
      pages.PlaceFieldsPage({
        onSave: finalOnSave,
      }),
    ],
  },
  EmbeddedRequestReviewWithTemplate: {
    requestType: RequestTypes.EmbeddedRequest,
    tags: [
      WorkflowTags.SignatureRequestTemplate,
    ],
    name: 'embedded request with template - show review',
    pages: [
      pages.EmbeddedPreLoadingPage({}),
      pages.PlaceFieldsPage({}),
      pages.ReviewPage({}),
    ],
  },
  EmbeddedRequestRecipientReviewWithTemplate: {
    requestType: RequestTypes.EmbeddedRequest,
    tags: [
      WorkflowTags.SignatureRequestTemplate,
    ],
    name: 'embedded request with template - show signers and review',
    pages: [
      pages.EmbeddedPreLoadingPage({}),
      pages.RecipientPage({}),
      pages.PlaceFieldsPage({}),
      pages.ReviewPage({}),
    ],
  },
  OfficeSelfSign: {
    requestType: RequestTypes.EmbeddedRequest,
    tags: [
      WorkflowTags.SignatureRequestDocument,
    ],
    name: 'office add in - self sign',
    pages: [
      pages.PlaceFieldsPage({
        onSave: finalOnSave,
      }),
    ],
  },
  Integration: {
    requestType: RequestTypes.EmbeddedRequest,
    tags: [
      WorkflowTags.SignatureRequestDocument,
    ],
    name: 'Integration - Signature Request',
    pages: [
      pages.EmbeddedPreLoadingPage({}),
      pages.RecipientPage({
        onSave: [
          actions.changeWorkFlowIntegrationSelfSign,
          ...defaultOnSave,
        ],
      }),
      pages.PlaceFieldsPage({}),
      pages.ReviewPage({}),
    ],
  },
  IntegrationOneTimeDoc: {
    requestType: RequestTypes.EmbeddedRequest,
    tags: [
      WorkflowTags.IntegrationOneTimeDoc,
      WorkflowTags.SignatureRequestDocument,
    ],
    name: 'Integration - One Time Document',
    pages: [
      pages.EmbeddedPreLoadingPage({}),
      pages.FileUploaderPage({}),
      pages.RecipientPage({}),
      pages.PlaceFieldsPage({}),
      pages.ReviewPage({}),
    ],
  },
  IntegrationSelfSign: {
    requestType: RequestTypes.EmbeddedRequest,
    tags: [
      WorkflowTags.SignatureRequestDocument,
    ],
    name: 'Integration - Self Sign',
    pages: [
      pages.EmbeddedPreLoadingPage({}),
      pages.RecipientPage({}),
      pages.PlaceFieldsPage({
        onSave: [actions.selfSaveAfterEditor],
      }),
    ],
  },
};

export interface SetWorkflowAction {
  type: Actions.SetWorkflow,
  payload: Workflow,
}

export type WorkflowActions =
  | SetWorkflowAction;
